import { useEffect } from 'react';

import Grid from '@mui/material/Grid2';
import { Box, Card, alpha, useTheme, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useMicrosoftTeamsAuthPopup } from 'src/hook/useMicrosoftTeamsAuthPopup';

import { Step1 } from './step1';
import { Step3 } from './step3';
import { Step4 } from './step4';
import { useChannelContext } from '../hooks';
import { ChannelSteps } from './channel-steps';
import LineStep2 from '../channels/Line/components/Step2';
import LineStep3 from '../channels/Line/components/Step3';
import { SMSStep2 } from '../channels/SMS/components/Step2';
import { SMSStep3 } from '../channels/SMS/components/Step3';
import { Step2 } from '../channels/Whatsapp/components/Step2';
import VoiceStep1 from '../channels/Voice/components/voiceStep1';
import VoiceStep2 from '../channels/Voice/components/voiceStep2';
import { EmailIMAP } from '../channels/Email/components/email-IMAP';
import { EmailSMTP } from '../channels/Email/components/email-SMTP';
import QRCodeCard from '../channels/Line/components/qrcode-display';
import { EmailStep2 } from '../channels/Email/components/email-step2';
import { EmailStep3 } from '../channels/Email/components/email-step3';
import { EmailStep5 } from '../channels/Email/components/email-step5';
import { FaceBookStep2 } from '../channels/Messenger/components/Step2';
import { InstagramStep2 } from '../channels/Instagram/components/Step2';
import { TeamsStep2 } from '../channels/MicrosoftTeams/components/Step2';
import LiveChatStep1 from '../channels/Livechat/components/liveChatStep1';
import { TelegramStep2 } from '../channels/Telegram/components/telegram-Step2';
import { useMicroSoftAuthPopup } from '../channels/Email/hooks/useMicroSoftAuthPopup';

// ----------------------------------------------------------------------

export function ChannelStepView() {
  const { t } = useTranslate('translation');
  const theme = useTheme();

  const { steps, activeStep, handleStepHeader, state: channelState } = useChannelContext();

  const { channelType, emailProviderType } = channelState;

  const { handleMicrosoftTeamsRedirect } = useMicrosoftTeamsAuthPopup();
  useMicroSoftAuthPopup();

  const renderEmailSteps = (activeStp: number | null) => {
    switch (activeStp) {
      case 1:
        return <EmailStep2 />;
      case 2:
        return <EmailStep3 />;
      case 3:
        return emailProviderType === 'other_provider' ? <EmailSMTP /> : <Step3 />;
      case 4:
        return emailProviderType === 'other_provider' ? <EmailIMAP /> : <Step4 />;
      case 5:
        return <Step3 />;
      case 6:
        return <Step4 />;
      default:
        return null;
    }
  };

  const renderLiveChatSteps = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <LiveChatStep1 />;
      case 2:
        return <Step3 />;
      case 3:
        return <EmailStep5 />;
      case 4:
        return <Step4 />;
      default:
        return null;
    }
  };

  const renderVoiceCallSteps = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <VoiceStep1 />;
      case 2:
        return <VoiceStep2 />;
      case 3:
        return <Step4 />;
      default:
        return null;
    }
  };

  const renderFaceBookStep = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <FaceBookStep2 />;
      case 2:
        return <Step3 />;
      case 3:
        return <Step4 />;
      default:
        return null;
    }
  };

  const renderInstagramStep = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <InstagramStep2 />;
      case 2:
        return <Step3 />;
      case 3:
        return <Step4 />;
      default:
        return null;
    }
  };
  const renderTelegramStep = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <TelegramStep2 />;
      case 2:
        return <Step3 />;
      case 3:
        return <Step4 />;
      default:
        return null;
    }
  };

  const renderSMSStep = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <SMSStep3 />;
      case 2:
        return <Step3 />;
      case 3:
        return <Step4 />;
      default:
        return null;
    }
  };

  const renderlineStep = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <LineStep2 />;
      case 2:
        return <LineStep3 />;
      case 3:
        return <QRCodeCard />;
      default:
        return null;
    }
  };
  const renderTeamsStep = (currentStep: number | null) => {
    switch (currentStep) {
      case 1:
        return <TeamsStep2 />;
      case 2:
        return <Step3 />;
      case 3:
        return <Step4 />;
      default:
        return null;
    }
  };

  useEffect(() => {
    handleMicrosoftTeamsRedirect();
  }, [handleMicrosoftTeamsRedirect]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <Card sx={{ m: { xs: 0, md: 2 }, px: { xs: 1.5, md: 2 }, py: { xs: 3, md: 5 } }}>
      <Grid container spacing={3}>
        {/* Sidebar - Steps Navigation */}
        <Grid size={{ xs: 12, lg: 3 }}>
          <Card sx={{ p: { xs: 2, md: 4 } }}>
            <ChannelSteps steps={steps} activeStep={activeStep ?? 0} channelState={channelState} />
          </Card>
        </Grid>

        {/* Main Content */}

        <Grid size={{ xs: 12, lg: 9 }}>
          <Card sx={{ height: '100%', p: { xs: 1.5, md: 3 } }}>
            {/* Header */}
            <Box
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                {handleStepHeader() ?? ''}
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ minHeight: 400 }}>
              {activeStep === 0 && <Step1 />}

              {/* WHATSAPP */}
              {channelType === 'WHATSAPP' && activeStep === 1 && <Step2 />}
              {channelType === 'WHATSAPP' && activeStep === 2 && <Step3 />}
              {channelType === 'WHATSAPP' && activeStep === 3 && <Step4 />}

              {/* EMAIL */}
              {channelType === 'EMAIL' && renderEmailSteps(activeStep)}

              {/* LIVE CHAT */}
              {channelType === 'LIVE_CHAT' && renderLiveChatSteps(activeStep)}

              {/* VOICE CALL */}
              {channelType === 'VOICE' && renderVoiceCallSteps(activeStep)}

              {/* FACEBOOK */}
              {channelType === 'MESSENGER' && renderFaceBookStep(activeStep)}

              {/* INSTAGRAM */}
              {channelType === 'INSTAGRAM' && renderInstagramStep(activeStep)}

              {/* SMS */}
              {channelType === 'SMS' && renderSMSStep(activeStep)}

              {/* LINE */}
              {channelType === 'LINE' && renderlineStep(activeStep)}

              {/* TELEGRAM */}
              {channelType === 'TELEGRAM' && renderTelegramStep(activeStep)}

              {/* MICROSOFTTEAMS */}
              {channelType === 'MICROSOFTTEAMS' && renderTeamsStep(activeStep)}
            </Box>

            {/* Progress Indicator */}
            {/* {activeStep !== null && (
              <Box
                sx={{ mt: 4, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('STEP')} {activeStep + 1} of {steps.length}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {steps.map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor:
                            index === activeStep
                              ? theme.palette.primary.main
                              : index < activeStep
                                ? theme.palette.success.main
                                : alpha(theme.palette.text.secondary, 0.2),
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )} */}
          </Card>
        </Grid>
      </Grid>
    </Card>
  );
}
